const CLIENT_TIME_ZONE_HEADER = "X-PB-Client-Time-Zone";
const CLIENT_UTC_OFFSET_HEADER = "X-PB-Client-Utc-Offset-Minutes";
const CLIENT_LANGUAGES_HEADER = "X-PB-Client-Languages";
const MAX_CLIENT_LANGUAGES = 16;
const MAX_CLIENT_LANGUAGE_LENGTH = 64;
const MAX_CLIENT_TIME_ZONE_LENGTH = 128;
const ClientTimeZonePattern =
    /^[A-Za-z0-9._+-]+(?:\/[A-Za-z0-9._+-]+)*$/u;

export interface ClientSessionSignalsInput {
    timeZone?: string | null;
    utcOffsetMinutes?: number | null;
    languages?: readonly string[] | null;
}

function normalizedTimeZone(value: string | null | undefined): string | null {
    const normalized = value?.trim();
    return normalized &&
        normalized.length <= MAX_CLIENT_TIME_ZONE_LENGTH &&
        ClientTimeZonePattern.test(normalized)
        ? normalized
        : null;
}

function normalizedLanguages(
    values: readonly string[] | null | undefined,
): string[] {
    const languages: string[] = [];
    const seen = new Set<string>();

    for (const value of values ?? []) {
        const trimmed = value.trim();
        if (!trimmed || trimmed.length > MAX_CLIENT_LANGUAGE_LENGTH) continue;

        let canonical: string;
        try {
            canonical = Intl.getCanonicalLocales(trimmed)[0] ?? "";
        } catch {
            continue;
        }
        if (!canonical) continue;

        const deduplicationKey = canonical.toLowerCase();
        if (seen.has(deduplicationKey)) continue;
        seen.add(deduplicationKey);
        languages.push(canonical);
        if (languages.length === MAX_CLIENT_LANGUAGES) break;
    }

    return languages;
}

export function buildClientSessionSignalHeaders(
    input: ClientSessionSignalsInput,
): Record<string, string> {
    const headers: Record<string, string> = {};
    const timeZone = normalizedTimeZone(input.timeZone);
    if (timeZone) headers[CLIENT_TIME_ZONE_HEADER] = timeZone;

    if (
        Number.isInteger(input.utcOffsetMinutes) &&
        input.utcOffsetMinutes != null &&
        input.utcOffsetMinutes >= -840 &&
        input.utcOffsetMinutes <= 840
    ) {
        headers[CLIENT_UTC_OFFSET_HEADER] = String(input.utcOffsetMinutes);
    }

    const languages = normalizedLanguages(input.languages);
    if (languages.length > 0) {
        headers[CLIENT_LANGUAGES_HEADER] = JSON.stringify(languages);
    }

    return headers;
}

export function browserClientSessionSignalHeaders(): Record<string, string> {
    let timeZone: string | null = null;
    try {
        timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    } catch {
        // Older or restricted browser environments may not expose a zone.
    }

    const languages =
        navigator.languages.length > 0
            ? navigator.languages
            : navigator.language
              ? [navigator.language]
              : [];

    return buildClientSessionSignalHeaders({
        timeZone,
        utcOffsetMinutes: -new Date().getTimezoneOffset(),
        languages,
    });
}
